import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { MODULE_GROUPS, ACTIONS, actionsFor, effectivePermissions, usesModulePerms }
  from '../permissionModules';

// Permissions manager — pick a role, then toggle module access (View/Create/Edit).
// Roles themselves are created under the Roles menu; here we only assign access.
export default function Permissions() {
  const { isAdmin } = useAuth();
  const [roles, setRoles]   = useState([]);
  const [roleId, setRoleId] = useState('');
  const [perms, setPerms]   = useState({});
  const [assetTypes, setAssetTypes] = useState([]);   // preserved as-is
  const [dirty, setDirty]   = useState(false);
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState('');

  const load = useCallback(() => api.get('/master/roles').then(r => setRoles(r.data)).catch(()=>{}), []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const role = roles.find(r => String(r.id) === String(roleId));

  // When a role is selected, pre-fill with its EFFECTIVE permissions
  // (base template merged with what's stored) so nothing is silently dropped.
  useEffect(() => {
    if (!role) { setPerms({}); setAssetTypes([]); setDirty(false); return; }
    setPerms(effectivePermissions(role.base_type, role.permissions || {}));
    setAssetTypes(role.permissions?.asset_types || []);
    setDirty(false);
  }, [roleId, roles]);

  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''), 3000); };

  const toggle = (mod, action) => {
    setPerms(p => {
      const cur = p[mod] || {};
      const next = { ...cur, [action]: !cur[action] };
      if (action === 'view' && !next.view) { next.create = false; next.edit = false; }
      if ((action === 'create' || action === 'edit') && next[action]) next.view = true;
      return { ...p, [mod]: next };
    });
    setDirty(true);
  };

  const setGroup = (modules, on) => {
    setPerms(p => {
      const next = { ...p };
      modules.forEach(m => {
        const acts = actionsFor(m.key).map(a => a.key);
        next[m.key] = { view: on && acts.includes('view'), create: on && acts.includes('create'), edit: on && acts.includes('edit') };
      });
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    if (!role) return;
    setBusy(true);
    try {
      const permissions = { ...perms, asset_types: assetTypes };
      await api.put(`/master/roles/${role.id}`, { permissions });
      flash('✓ Permissions saved — affected users see the change on their next login.');
      await load(); setDirty(false);
    } catch (e) { flash(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const portalRole = role && !usesModulePerms(role.base_type);

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>🔑 Permissions</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>
          Pick a role and choose which modules it can access. Create roles under <b>Roles</b>; assign them to people under <b>Users</b>.
        </p>
      </div>

      <div className="card" style={{ marginBottom:16, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <div className="form-group" style={{ margin:0, minWidth:260 }}>
          <label>Role</label>
          <select value={roleId} onChange={e => setRoleId(e.target.value)}>
            <option value="">— Select a role —</option>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}{r.is_builtin ? ' (built-in)' : ''}</option>)}
          </select>
        </div>
        {role && <div style={{ fontSize:12, color:'var(--text2)' }}>Base experience: <b>{role.base_type}</b></div>}
        {dirty && <span style={{ fontSize:12, color:'var(--amber)' }}>Unsaved changes</span>}
        <div style={{ marginLeft:'auto' }}>
          <button className="btn btn-primary" disabled={!role || portalRole || busy || !dirty} onClick={save}>
            {busy ? 'Saving…' : 'Save permissions'}
          </button>
        </div>
      </div>

      {msg && <div style={{ fontSize:13, marginBottom:12, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}

      {!role ? (
        <div className="card"><div className="empty-state"><p>Select a role above to manage its module access.</p></div></div>
      ) : portalRole ? (
        <div className="card"><div className="empty-state">
          <p>The <b>{role.name}</b> role uses a fixed portal experience ({role.base_type}) — module permissions don't apply to it.</p>
        </div></div>
      ) : (
        MODULE_GROUPS.map(g => {
          return (
            <div key={g.group} className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg3)', borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text2)' }}>{g.group}</span>
                <div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setGroup(g.modules, true)}>All</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setGroup(g.modules, false)}>None</button>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(3, 90px)', padding:'8px 14px', borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontSize:11, fontWeight:600, color:'var(--text2)' }}>Module</span>
                {ACTIONS.map(a => <span key={a.key} style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textAlign:'center' }}>{a.label}</span>)}
              </div>
              {g.modules.map((m, i) => {
                const acts = actionsFor(m.key).map(a => a.key);
                return (
                  <div key={m.key} style={{ display:'grid', gridTemplateColumns:'1fr repeat(3, 90px)', alignItems:'center', padding:'9px 14px', borderBottom: i < g.modules.length-1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ fontSize:13, fontWeight:500 }}>{m.label}</span>
                    {ACTIONS.map(a => (
                      <div key={a.key} style={{ textAlign:'center' }}>
                        {acts.includes(a.key)
                          ? <input type="checkbox" checked={!!perms[m.key]?.[a.key]} onChange={() => toggle(m.key, a.key)} style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--accent)' }} />
                          : <span style={{ color:'var(--text3)' }}>—</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}
